/**
 * BuilderSettingsPanel — F12.H T12.H.10 cz.C.
 *
 * Boczny overlay (z prawej, jak SellCall) z ustawieniami całego layoutu
 * modułu — w odróżnieniu od right panelu który edytuje POJEDYNCZY zaznaczony
 * element. Tu user konfiguruje globalne właściwości formularza:
 *  - Formularz: szerokość, odstępy, pozycja etykiet, top-tabs toggle
 *  - Pola: master-lista pól z badge'ami „na formularzu" / „nie używane"
 *  - Numeracja: link do strony konfiguracji numeracji (T12.I)
 *
 * Zapis: settings zapisywane do `layout_config.settings` (rozszerzenie
 * istniejącego JSONB). Render formularza (RecordFormPage) może je czytać
 * dla globalnych preferencji wizualnych — wpadnie w T12.H.12 final pass.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Hash, LayoutGrid, ListChecks } from 'lucide-react';

import { Button } from '@/components/Button';
import { Drawer } from '@/components/Drawer';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';

export interface LayoutSettings {
  width?: 'narrow' | 'medium' | 'wide';
  sectionSpacing?: 'compact' | 'normal' | 'spacious';
  labelPosition?: 'top' | 'left';
  tabsEnabled?: boolean;
}

interface FieldMasterItem {
  id: string;
  name: string;
  slug: string;
  fieldType: string;
  onCanvas: boolean;
}

interface BuilderSettingsPanelProps {
  open: boolean;
  onClose: () => void;
  moduleId: string;
  settings: LayoutSettings;
  onSettingsChange: (patch: Partial<LayoutSettings>) => void;
  fields: FieldMasterItem[];
}

type SubTab = 'form' | 'fields' | 'numbering';

export function BuilderSettingsPanel({
  open,
  onClose,
  moduleId,
  settings,
  onSettingsChange,
  fields,
}: BuilderSettingsPanelProps) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<SubTab>('form');

  // Zachowanie (Esc, tło, fokus, blokada przewijania, portal) daje `Drawer` na wspólnym rdzeniu.
  return (
    <Drawer open={open} onClose={onClose} title="Ustawienia układu" testId="builder-settings-panel">
      {/* Sub-taby — pełna szerokość ciała okna: ujemne marginesy `-mx-5 -mt-4` kasują padding ciała
          `Drawer` (`px-5 py-4`, patrz `Drawer.tsx`) — przy zmianie tego paddingu zmień je razem. */}
      <div
        className="-mx-5 -mt-4 mb-4 flex border-b border-border-primary bg-bg-secondary"
        role="tablist"
      >
        <TabBtn
          active={tab === 'form'}
          onClick={() => setTab('form')}
          icon={<LayoutGrid className="h-3.5 w-3.5" />}
          label="Formularz"
          testId="builder-settings-tab-form"
        />
        <TabBtn
          active={tab === 'fields'}
          onClick={() => setTab('fields')}
          icon={<ListChecks className="h-3.5 w-3.5" />}
          label="Pola"
          testId="builder-settings-tab-fields"
        />
        <TabBtn
          active={tab === 'numbering'}
          onClick={() => setTab('numbering')}
          icon={<Hash className="h-3.5 w-3.5" />}
          label="Numeracja"
          testId="builder-settings-tab-numbering"
        />
      </div>

      {tab === 'form' && <FormSubTab settings={settings} onChange={onSettingsChange} />}
      {tab === 'fields' && <FieldsSubTab moduleId={moduleId} fields={fields} navigate={navigate} />}
      {tab === 'numbering' && <NumberingSubTab moduleId={moduleId} navigate={navigate} />}
    </Drawer>
  );
}

// ─── Sub-tab buttons ────────────────────────────────────────────────────────

function TabBtn({
  active,
  onClick,
  icon,
  label,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      className={`flex flex-1 items-center justify-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
        active
          ? 'border-accent-500 text-accent-600 bg-bg-primary'
          : 'border-transparent text-text-secondary hover:text-text-primary'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

// ─── Sub-tab: Formularz ─────────────────────────────────────────────────────

function FormSubTab({
  settings,
  onChange,
}: {
  settings: LayoutSettings;
  onChange: (patch: Partial<LayoutSettings>) => void;
}) {
  return (
    <div className="space-y-4">
      <FormField label="Szerokość formularza">
        <Dropdown
          value={settings.width ?? 'medium'}
          onChange={(v) => onChange({ width: v as LayoutSettings['width'] })}
          className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
          data-testid="builder-settings-width"
          options={[
            { value: 'narrow', label: 'Wąski (~600px)' },
            { value: 'medium', label: 'Średni (~900px)' },
            { value: 'wide', label: 'Szeroki (na całą stronę)' },
          ]}
        />
      </FormField>

      <FormField label="Odstępy między sekcjami">
        <Dropdown
          value={settings.sectionSpacing ?? 'normal'}
          onChange={(v) => onChange({ sectionSpacing: v as LayoutSettings['sectionSpacing'] })}
          className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
          data-testid="builder-settings-spacing"
          options={[
            { value: 'compact', label: 'Kompaktowe' },
            { value: 'normal', label: 'Normalne' },
            { value: 'spacious', label: 'Rozłożone' },
          ]}
        />
      </FormField>

      <FormField label="Pozycja etykiet pól">
        <Dropdown
          value={settings.labelPosition ?? 'top'}
          onChange={(v) => onChange({ labelPosition: v as LayoutSettings['labelPosition'] })}
          className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
          data-testid="builder-settings-label-position"
          options={[
            { value: 'top', label: 'Nad polem' },
            { value: 'left', label: 'Z lewej strony' },
          ]}
        />
      </FormField>

      <div className="rounded-md border border-border-primary bg-bg-secondary p-3">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={settings.tabsEnabled === true}
            onChange={(e) => onChange({ tabsEnabled: e.target.checked })}
            className="mt-0.5 h-4 w-4 rounded border-border-primary text-accent-600"
            data-testid="builder-settings-tabs-enabled"
          />
          <span>
            <span className="block font-medium text-text-primary">Top-tabs formularza</span>
            <span className="block text-xs text-text-tertiary">
              Włącz globalne zakładki na formularzu (sekcje są pogrupowane w zakładki na górze).
              Render kontrolowany w T12.H.12.
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}

// ─── Sub-tab: Pola ──────────────────────────────────────────────────────────

function FieldsSubTab({
  moduleId,
  fields,
  navigate,
}: {
  moduleId: string;
  fields: FieldMasterItem[];
  navigate: ReturnType<typeof useNavigate>;
}) {
  const onCanvas = fields.filter((f) => f.onCanvas);
  const unused = fields.filter((f) => !f.onCanvas);

  return (
    <div className="space-y-4">
      <p className="text-xs text-text-tertiary">
        Master-lista wszystkich pól modułu. Edycja właściwości pola (typ, walidacja, opcje) odbywa
        się w pełnym edytorze pól.
      </p>

      <Button
        variant="secondary"
        size="sm"
        onClick={() => navigate(`/modules/${moduleId}/settings?tab=fields`)}
        rightIcon={<ExternalLink className="h-3 w-3" />}
        data-testid="builder-settings-fields-link"
      >
        Otwórz edytor pól modułu
      </Button>

      {onCanvas.length > 0 && (
        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted">
            Na formularzu ({onCanvas.length})
          </p>
          <div className="space-y-1">
            {onCanvas.map((f) => (
              <FieldRow key={f.id} field={f} badge="canvas" />
            ))}
          </div>
        </div>
      )}

      {unused.length > 0 && (
        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted">
            Niewykorzystane ({unused.length})
          </p>
          <div className="space-y-1">
            {unused.map((f) => (
              <FieldRow key={f.id} field={f} badge="unused" />
            ))}
          </div>
        </div>
      )}

      {fields.length === 0 && (
        <p className="text-xs text-text-tertiary">Moduł nie ma jeszcze żadnych pól.</p>
      )}
    </div>
  );
}

function FieldRow({ field, badge }: { field: FieldMasterItem; badge: 'canvas' | 'unused' }) {
  return (
    <div
      className="flex items-center justify-between rounded-md border border-border-primary bg-bg-primary px-2 py-1.5"
      data-testid={`builder-settings-field-${field.slug}`}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-text-primary">{field.name}</div>
        <div className="truncate text-xs text-text-tertiary">
          /{field.slug} · {field.fieldType}
        </div>
      </div>
      <span
        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
          badge === 'canvas' ? 'bg-success-50 text-success-700' : 'bg-warning-50 text-warning-700'
        }`}
      >
        {badge === 'canvas' ? 'Na formularzu' : 'Nieużywane'}
      </span>
    </div>
  );
}

// ─── Sub-tab: Numeracja ─────────────────────────────────────────────────────

function NumberingSubTab({
  moduleId,
  navigate,
}: {
  moduleId: string;
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-text-secondary">
        Konfiguracja schematu numeracji rekordów (np. <code>FV/2026/{'{nr}'}</code>) odbywa się w
        pełnym edytorze numeracji w ustawieniach modułu.
      </p>
      <Button
        variant="primary"
        size="sm"
        onClick={() => navigate(`/modules/${moduleId}/settings?tab=numbering`)}
        rightIcon={<ExternalLink className="h-3.5 w-3.5" />}
        data-testid="builder-settings-numbering-link"
      >
        Otwórz konfigurację numeracji
      </Button>
      <p className="text-xs text-text-tertiary">Pełen edytor zostanie wdrożony w F12.I.</p>
    </div>
  );
}
